import React, { useState } from 'react';
import { Search, Shield, Filter, CheckCircle2, XCircle, Globe, Lock } from 'lucide-react';
import { USER_ACCESS_MATRIX, AccessMatrixItem } from '../data/userAccessMatrixData';

export default function UserAccessMatrixTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState<string>('ALL');

  const modules = Array.from(new Set(USER_ACCESS_MATRIX.map(item => item.module)));

  const filteredItems = USER_ACCESS_MATRIX.filter(item => {
    const matchesModule = selectedModule === 'ALL' || item.module === selectedModule;
    const matchesSearch = !searchTerm || 
      item.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subModule.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.accessLevel.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesModule && matchesSearch;
  });

  const renderBadge = (level: 'FULL' | 'DENIED' | 'PUBLIC') => {
    if (level === 'FULL') {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800 px-2 py-0.5 rounded-full">
          <CheckCircle2 className="w-3 h-3 text-emerald-600" /> FULL
        </span>
      );
    }
    if (level === 'PUBLIC') {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 px-2 py-0.5 rounded-full">
          <Globe className="w-3 h-3 text-blue-600" /> PUBLIC
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900/60 px-2 py-0.5 rounded-full">
        <XCircle className="w-3 h-3 text-red-500" /> DENIED
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <h3 className="text-base font-extrabold text-black dark:text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-600" /> Armellini CRM Role Permission Matrix
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            System-wide access matrix detailing endpoint and UI capabilities across Admin, Editor, Sales, and Warehouse roles.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Module Filter */}
          <div className="relative">
            <select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 px-3 py-1.5 outline-none focus:border-blue-500"
            >
              <option value="ALL">All Modules ({USER_ACCESS_MATRIX.length})</option>
              {modules.map(mod => (
                <option key={mod} value={mod}>{mod}</option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div className="relative w-48 sm:w-64">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search permissions..."
              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Access Matrix Table */}
      <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm bg-white dark:bg-slate-950">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 font-extrabold uppercase tracking-wider text-[11px]">
              <th className="py-3 px-3.5">Module / Category</th>
              <th className="py-3 px-3.5">Sub-Module / Area</th>
              <th className="py-3 px-3.5">Action / Permission</th>
              <th className="py-3 px-3.5 font-mono">Access Level / Endpoint</th>
              <th className="py-3 px-2 text-center">Admin</th>
              <th className="py-3 px-2 text-center">Editor</th>
              <th className="py-3 px-2 text-center">Sales</th>
              <th className="py-3 px-2 text-center">Warehouse</th>
              <th className="py-3 px-3.5">Security Rules & Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-8 text-center text-slate-400">
                  No permission items match your filter criteria.
                </td>
              </tr>
            ) : (
              filteredItems.map((item, idx) => (
                <tr 
                  key={`${item.module}-${item.action}-${idx}`}
                  className="hover:bg-slate-50/70 dark:hover:bg-slate-900/40 transition-colors"
                >
                  <td className="py-2.5 px-3.5 font-bold text-slate-900 dark:text-slate-100 shrink-0">
                    <span className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-[11px]">
                      {item.module}
                    </span>
                  </td>
                  <td className="py-2.5 px-3.5 font-medium text-slate-700 dark:text-slate-300">
                    {item.subModule}
                  </td>
                  <td className="py-2.5 px-3.5 font-bold text-slate-800 dark:text-slate-200">
                    {item.action}
                  </td>
                  <td className="py-2.5 px-3.5 font-mono text-[11px] text-blue-600 dark:text-blue-400">
                    {item.accessLevel}
                  </td>
                  <td className="py-2.5 px-2 text-center">{renderBadge(item.admin)}</td>
                  <td className="py-2.5 px-2 text-center">{renderBadge(item.editor)}</td>
                  <td className="py-2.5 px-2 text-center">{renderBadge(item.sales)}</td>
                  <td className="py-2.5 px-2 text-center">{renderBadge(item.warehouse)}</td>
                  <td className="py-2.5 px-3.5 text-[11px] text-slate-500 dark:text-slate-400 max-w-xs">
                    {item.description}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-[11px] text-slate-400 px-1">
        <span>Showing {filteredItems.length} of {USER_ACCESS_MATRIX.length} security rules</span>
        <span className="flex items-center gap-1"><Lock className="w-3 h-3 text-slate-400" /> Managed by System Administrators</span>
      </div>
    </div>
  );
}
